import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useServiceWorker } from './hooks/useServiceWorker';
import { useAppBadge } from './hooks/useAppBadge';
import { updateAppMetadata } from './utils/updateAppMetadata';
import LandingPage from './LandingPage';
import Login from './components/auth/Login';
import RegisterClub from './components/auth/RegisterClub';
import RegistrationGuard from './components/auth/RegistrationGuard';
import AdminDashboard from './components/admin/AdminDashboard';
import PlayerDashboard from './components/player/PlayerDashboard';
import AuthAction from './components/auth/AuthAction';
import TermsPage from './components/auth/TermsPage';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './services/firebase';

// EnvironmentBadge conditionally rendered
const EnvironmentBadge = () => null;

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles, userRole, clubInfoRole, additionalRoles = [], isAuthenticated, isNativeClubAdmin }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If the permissions array natively verifies they are a Club Admin for this active session, guarantee access:
  if (isNativeClubAdmin) {
    return children;
  }

  // Check userRole, clubInfoRole, AND additionalRoles
  const effectiveRole = userRole || clubInfoRole;

  // Check if primary role matches
  const hasPrimaryRole = allowedRoles && (allowedRoles.includes(effectiveRole) || allowedRoles.includes(clubInfoRole));

  // Check if any additional role matches
  const hasAdditionalRole = allowedRoles && additionalRoles.some(role => allowedRoles.includes(role));

  if (allowedRoles && !hasPrimaryRole && !hasAdditionalRole) {
    console.warn('⛔ ProtectedRoute Rejected Navigation:', {
      path: window.location.pathname,
      effectiveRole,
      clubInfoRole,
      userRole,
      allowedRoles,
      isNativeClubAdmin
    });
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { currentUser, userRole, clubInfo, loading, login, signup, logout } = useAuth();

  // Initialize service worker for auto-updates
  useServiceWorker();

  // Initialize App Badge (PWA)
  useAppBadge(currentUser);

  // Update app metadata (favicon, title, etc.) when club info is loaded
  useEffect(() => {
    if (clubInfo) {
      console.log('🎨 Updating app metadata with club info:', {
        name: clubInfo.name,
        logo: clubInfo.logo,
        hasLogo: !!clubInfo.logo
      });

      // Update favicon, page title, and other metadata with club info
      updateAppMetadata(clubInfo);
    }
  }, [clubInfo]);

  // Get effective role - check both user.role and clubInfo.role
  const clubInfoRole = currentUser?.clubInfo?.role || clubInfo?.role;
  const effectiveRole = userRole || clubInfoRole;

  // Get additional roles
  const additionalRoles = currentUser?.clubInfo?.additionalRoles || currentUser?.additionalRoles || [];

  console.log('🔵 App render - User:', currentUser);
  console.log('🔵 App render - Role:', userRole);
  console.log('🔵 App render - ClubInfo Role:', clubInfoRole);
  console.log('🔵 App render - Effective Role:', effectiveRole);
  console.log('🔵 App render - Loading:', loading);

  // EMERGENCY RECOVERY PATCH - SCOPED TO CWC ONLY
  // EMERGENCY RECOVERY PATCH - FORCING CWC ADMIN FOR KASH
  useEffect(() => {
    if (!loading && userRole === 'player' && currentUser) {
      const recoverScopedAdmin = async (cwcId) => {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          
          let currentAdminOf = currentUser.permissions?.clubAdminOf || [];
          if (cwcId) {
            // Add to clubAdminOf if not already there
            if (!currentAdminOf.includes(cwcId)) {
              currentAdminOf.push(cwcId);
            }
            
            // Build the patch payload
            const patchFields = {
              'permissions.clubAdminOf': currentAdminOf,
              [`joinedClubs.${cwcId}.role`]: 'admin' // Force legacy role
            };
            
            console.log('✅ Brute-forcing CWC Admin Recovery:', patchFields);
            await updateDoc(userRef, patchFields);
            
            // If they are actively viewing CWC, hot-patch the session so we don't need a reload loop
            if (currentUser.clubId === cwcId && currentUser.clubInfo?.role !== 'admin') {
               console.log('🔄 Hot-patching active session variables for instantaneous access');
               // (Optional: trigger a hard reload here if UI doesn't refresh)
               // window.location.reload(); 
            }
          }
        } catch (error) {
          console.error('❌ Failed to force admin recovery:', error);
        }
      };

      let initialCwcId = null;
      // Find the CWC club id dynamically
      if (currentUser.joinedClubs) {
        Object.entries(currentUser.joinedClubs).forEach(([id, data]) => {
          if (data.clubName?.includes('Cascadia') || data.clubName === 'CWC') {
            initialCwcId = id;
          }
        });
      }

      // If we found CWC and they are stuck at 'player' globally but need admin scoping
      if (!loading && userRole === 'player' && initialCwcId) {
         // ONLY RUN ONCE to prevent infinite loop
         const isAlreadyComplete = currentUser.permissions?.clubAdminOf?.includes(initialCwcId) 
                                && currentUser.joinedClubs?.[initialCwcId]?.role === 'admin';
         if (!isAlreadyComplete) recoverScopedAdmin(initialCwcId);
      }
    }
  }, [currentUser, loading, userRole]);


  // Determine redirect path based on user role
  const getRedirectPath = () => {
    if (!currentUser) return '/';
    // Super admins go directly to admin dashboard (they're not associated with any club)
    if (effectiveRole === 'super-admin') return '/admin';

    // Everyone else (including Admins and Treasurers) goes to the player dashboard by default
    return '/dashboard';
  };

  // DEBUG: Log role detection
  useEffect(() => {
    console.group('🔍 App.jsx Role Debug');
    console.log('User Role (from auth):', userRole);
    console.log('Club Info Role:', clubInfoRole);
    console.log('Effective Role:', effectiveRole);
    const additionalRoles = currentUser?.clubInfo?.additionalRoles || currentUser?.additionalRoles || [];
    console.log('Additional Roles:', additionalRoles);
    console.log('Redirect Path:', getRedirectPath());
    console.groupEnd();
  }, [userRole, clubInfoRole, effectiveRole, currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ClubHudl...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Environment Badge - Shows staging/dev banner, hidden in production */}
      <EnvironmentBadge />

      <Router>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              currentUser ? (
                <Navigate to={getRedirectPath()} replace />
              ) : (
                <LandingPage />
              )
            }
          />

          {/* Login/Signup Route */}
          <Route
            path="/login"
            element={
              currentUser ? (
                <Navigate to={getRedirectPath()} replace />
              ) : (
                <Login onLogin={login} onSignup={signup} />
              )
            }
          />

          {/* Register Club Route */}
          <Route path="/register-club" element={<RegisterClub />} />

          {/* Universal Auth Action Handler - handles all Firebase email actions */}
          <Route path="/auth-action" element={<AuthAction />} />

          {/* Terms of Service - Public route */}
          <Route path="/terms" element={<TermsPage />} />

          {/* Admin Routes - Only accessible if user is admin/super-admin */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute
                isAuthenticated={!!currentUser}
                userRole={userRole}
                clubInfoRole={clubInfoRole}
                additionalRoles={currentUser?.clubInfo?.additionalRoles || []}
                allowedRoles={['admin', 'super-admin', 'treasurer']}
                isNativeClubAdmin={currentUser?.permissions?.clubAdminOf?.includes(currentUser?.clubId || currentUser?.clubInfo?.clubId)}
              >
                <AdminDashboard user={currentUser} onLogout={logout} />
              </ProtectedRoute>
            }
          />

          {/* Player Routes - Accessible by all authenticated users */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute
                isAuthenticated={!!currentUser}
                userRole={userRole}
                clubInfoRole={clubInfoRole}
                allowedRoles={['player', 'admin', 'super-admin']}
              >
                <PlayerDashboard user={currentUser} onLogout={logout} />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;